import { ICONS } from '~/constants/icons';
import { DataFieldConfig } from './data-builder';
import { filterProviderData } from './utils';

/**
 * Shared building field configurations that can be reused across different building types
 * These fields use consistent labeling, icons, and formatting
 */
export const sharedBuildingFields: Record<string, DataFieldConfig> = {
  // Basic building information
  buildingType: {
    key: 'buildingType',
    label: 'Byggnadstyp',
    icon: ICONS.BUILDING.CATEGORY,
  },

  buildingYear: {
    key: 'yearBuilt',
    label: 'Byggår',
    icon: ICONS.BUILDING.YEAR,
    formatter: (value, context) => {
      const commentary =
        context?.filteredProviderData?.building?.commentaryForBuildingYear ||
        context?.filteredProviderData?.buildingYearComment;
      return commentary ? `${value} (${commentary})` : value;
    },
  },

  description: {
    key: 'description',
    label: 'Beskrivning',
    icon: ICONS.BUILDING.OTHER,
  },

  // Areas and spaces
  livingSpace: {
    key: 'livingSpace',
    label: 'Boarea',
    icon: ICONS.LIVING_SPACE,
    formatter: (value) => `${value} m²`,
  },

  buildingSpace: {
    key: 'buildingSpace',
    label: 'Byggnadsyta',
    icon: ICONS.BUILDING.CATEGORY,
    formatter: (value) => `${value} m²`,
  },

  plot: {
    key: 'plot',
    label: 'Tomt',
    icon: ICONS.PLOT_SIZE,
    formatter: (value) => value.toString(),
  },

  otherSpace: {
    key: 'otherSpace',
    label: 'Övriga ytor',
    icon: ICONS.ADDITIONAL_SPACE,
    formatter: (value) => `${value} m²`,
  },

  biArea: {
    key: 'biArea',
    label: 'Biarea',
    icon: ICONS.ADDITIONAL_SPACE,
    formatter: (value) => `${value} m²`,
  },

  // Rooms and floors
  numberOfRooms: {
    key: 'numberOfRooms',
    label: 'Antal rum',
    icon: ICONS.ROOMS,
    formatter: (value) => value.toString(),
  },

  numberOfBedrooms: {
    key: 'numberOfBedRooms',
    label: 'Varav sovrum',
    icon: ICONS.BEDROOMS,
    formatter: (value) => {
      if (value?.minValue || value?.maxValue) {
        const min = value.minValue;
        const max = value.maxValue;
        const formattedValue =
          min && max && min !== max
            ? `${min}-${max}`
            : (min || max)?.toString();
        return `${formattedValue} st`;
      }
      return `${value} st`;
    },
  },

  numberOfFloors: {
    key: 'floorAndElevator.numberOfFloors',
    label: 'Våningar',
    icon: ICONS.FLOOR,
    formatter: (value) => value.toString(),
  },

  elevator: {
    key: 'floorAndElevator.elevator',
    label: 'Hiss',
    icon: ICONS.BUILDING.ELEVATOR,
    formatter: (value, context) => {
      // Translate Yes/No to Ja/Nej
      const translatedValue = filterProviderData(value);
      const description =
        context?.filteredProviderData?.floorAndElevator?.descriptionOfElevator;
      return `${translatedValue}${description ? ` (${description})` : ''}`;
    },
  },

  // Building construction details
  facade: {
    key: 'building.facade',
    label: 'Fasad',
    icon: ICONS.BUILDING.FACADE,
  },

  frame: {
    key: 'building.frame',
    label: 'Stomme',
    icon: ICONS.BUILDING.FRAME,
  },

  foundation: {
    key: 'building.foundation',
    label: 'Grund',
    icon: ICONS.BUILDING.FOUNDATION,
  },

  foundationWall: {
    key: 'building.foundationWall',
    label: 'Grundmur',
    icon: ICONS.BUILDING.FOUNDATION_WALL,
  },

  roof: {
    key: 'building.roof',
    label: 'Tak',
    icon: ICONS.BUILDING.ROOF,
  },

  windows: {
    key: 'building.windows',
    label: 'Fönster',
    icon: ICONS.BUILDING.WINDOWS,
  },

  beam: {
    key: 'building.beam',
    label: 'Bjälklag',
    icon: ICONS.BUILDING.BEAM,
  },

  externallyMetalWork: {
    key: 'building.externallyMetalWork',
    label: 'Yttertak- och plåtarbeten',
    icon: ICONS.BUILDING.EXTERNALLY_SHEET_METAL_WORK,
  },

  // Systems
  heating: {
    key: 'building.heating',
    label: 'Uppvärmning',
    icon: ICONS.BUILDING.HEATING,
  },

  waterAndDrain: {
    key: 'building.waterAndDrain',
    label: 'Vatten och avlopp',
    icon: ICONS.BUILDING.WATER,
  },

  ventilation: {
    key: 'building.ventilation.type',
    label: 'Ventilation',
    icon: ICONS.BUILDING.VENTILATION,
  },

  cold: {
    key: 'building.cold',
    label: 'Kyla',
    icon: ICONS.BUILDING.VENTILATION,
  },

  // Energy
  primaryEnergyNumber: {
    key: 'energyDeclaration.primaryEnergyNumber',
    label: 'Energiprestanda',
    icon: ICONS.BUILDING.ENERGY_CONSUMPTION,
    formatter: (value) => `${value} kWh/kvm och år`,
  },

  energyClass: {
    key: 'energyDeclaration.energyClass',
    label: 'Energiklass',
    icon: ICONS.BUILDING.ENERGY_CLASSIFICATION,
  },

  // energyConsumption: {
  //   key: 'energyDeclaration.energyConsumption',
  //   label: 'Energiförbrukning',
  //   icon: ICONS.BUILDING.ENERGY_CONSUMPTION,
  // },

  // Technology
  tv: {
    key: 'tvAndBroadband.tv',
    label: 'TV',
    icon: ICONS.BUILDING.TV,
  },

  broadband: {
    key: 'tvAndBroadband.broadband',
    label: 'Bredband',
    icon: ICONS.BUILDING.INTERNET,
  },

  // Additional info
  renovations: {
    key: 'renovations',
    label: 'Renoveringar',
    icon: ICONS.BUILDING.RENOVATIONS,
  },

  generalDescription: {
    key: 'generalDescription',
    label: 'Allmän beskrivning',
    icon: ICONS.BUILDING.CATEGORY,
  },

  controlMonitoring: {
    key: 'building.controlMonitoring',
    label: 'Kontroll/övervakning',
    icon: ICONS.BUILDING.OTHER,
  },

  others: {
    key: 'building.others',
    label: 'Övrigt',
    icon: ICONS.BUILDING.OTHER,
  },
};

/**
 * Helper function to get field configurations for commercial buildings
 * Returns an array of field configs in the order they should appear
 */
export const getCommercialBuildingFieldConfigs = (): DataFieldConfig[] => [
  sharedBuildingFields.buildingType,
  sharedBuildingFields.buildingYear,
  sharedBuildingFields.description,
  sharedBuildingFields.livingSpace,
  sharedBuildingFields.buildingSpace,
  sharedBuildingFields.biArea,
  sharedBuildingFields.numberOfRooms,
  sharedBuildingFields.numberOfBedrooms,
  sharedBuildingFields.numberOfFloors,
  sharedBuildingFields.elevator,
  sharedBuildingFields.facade,
  sharedBuildingFields.frame,
  sharedBuildingFields.foundation,
  sharedBuildingFields.foundationWall,
  sharedBuildingFields.roof,
  sharedBuildingFields.windows,
  sharedBuildingFields.beam,
  sharedBuildingFields.externallyMetalWork,
  sharedBuildingFields.heating,
  sharedBuildingFields.waterAndDrain,
  sharedBuildingFields.ventilation,
  sharedBuildingFields.cold,
  sharedBuildingFields.controlMonitoring,
  sharedBuildingFields.others,
  sharedBuildingFields.primaryEnergyNumber,
  sharedBuildingFields.energyClass,
  // sharedBuildingFields.energyConsumption, // Commented out
  sharedBuildingFields.tv,
  sharedBuildingFields.broadband,
  sharedBuildingFields.renovations,
  sharedBuildingFields.generalDescription,
];

/**
 * Helper function to get field configurations for farms
 * Returns an array of field configs in the order they should appear
 */
export const getFarmResidentialBuildingFieldConfigs = (): DataFieldConfig[] => [
  sharedBuildingFields.buildingType,
  sharedBuildingFields.buildingYear,
  sharedBuildingFields.yearBuilt,
  sharedBuildingFields.description,
  sharedBuildingFields.livingSpace,
  sharedBuildingFields.buildingSpace,
  sharedBuildingFields.otherSpace,
  sharedBuildingFields.numberOfRooms,
  sharedBuildingFields.heating,
  sharedBuildingFields.waterAndDrain,
  sharedBuildingFields.ventilation,
  sharedBuildingFields.others,
  sharedBuildingFields.plot,
  sharedBuildingFields.energyDeclaration,
  sharedBuildingFields.tv,
  sharedBuildingFields.broadband,
  sharedBuildingFields.renovations,
  sharedBuildingFields.generalDescription,
];
